import { AuthorDAO } from './dao/AuthorDAO.js';
import { BlogDAO } from './dao/BlogDAO.js';
import { TagDAO } from './dao/TagDAO.js';

export class AuthorService {
  constructor() {
    this.authorDAO = new AuthorDAO();
    this.blogDAO = new BlogDAO();
    this.tagDAO = new TagDAO(this.blogDAO);
  }

  // CORE AUTHOR OPERATIONS

  // Get author profile with comprehensive information
  async getAuthorProfile(authorSlug) {
    try {
      const author = await this.authorDAO.getAuthor(authorSlug);
      if (!author) return null;

      // Get author's stories
      const authorStories = await this.getAuthorStories(authorSlug);
      
      // Calculate enhanced stats
      const stats = await this.calculateAuthorStats(authorSlug, authorStories);
      
      // Get author's expertise areas
      const expertise = await this.getAuthorExpertise(authorSlug, authorStories);

      return {
        ...author,
        slug: authorSlug,
        stats,
        expertise,
        recentStories: authorStories.slice(0, 5), // 5 most recent
        profileImage: author.profileImage || this.getDefaultProfileImage(authorSlug),
        socialLinks: author.socialLinks || {},
        joinDate: author.joinDate || null,
        verified: author.verified || false
      };
    } catch (error) {
      console.error(`Failed to get author profile: ${authorSlug}`, error);
      return null;
    }
  }

  // Get author's published stories
  async getAuthorStories(authorSlug, options = {}) {
    const {
      limit = null,
      offset = 0,
      includeUnpublished = false,
      sortBy = 'date',
      sortOrder = 'desc'
    } = options;

    try {
      const blogDb = await this.blogDAO.getBlogDatabase();
      const authorStories = [];

      // Find all stories by this author
      const stories = blogDb.stories.filter(story => story.author_slug === authorSlug);

      for (const story of stories) {
        // Check if should be included
        if (!includeUnpublished && !this.blogDAO.shouldShow(story.publish_date, story.publishStatus)) {
          continue;
        }

        if (story.type === 'standalone') {
          const storyData = await this.blogDAO.getStoryMetadata(story.id, null, true);
          if (storyData) {
            authorStories.push({
              id: story.id,
              title: storyData.title,
              excerpt: storyData.excerpt,
              publishDate: story.publish_date,
              category: story.category,
              tags: await this.tagDAO.getTagNames(story.tags || []),
              readTime: storyData.readTime,
              coverImage: storyData.coverImage,
              featured: story.featured,
              type: 'standalone',
              slug: this.blogDAO.findRoutingKey(blogDb.routing, story.id, null, 'standalone')
            });
          }
        } else if (story.type === 'series') {
          const seriesData = await this.blogDAO.getSeriesMetadata(story.id);
          if (seriesData && seriesData.parts) {
            for (const part of seriesData.parts) {
              const partDate = part.publishDate || story.publish_date;
              if (!includeUnpublished && !this.blogDAO.shouldShow(partDate, part.status || 'published')) {
                continue;
              }

              const partData = await this.blogDAO.getStoryMetadata(story.id, part.partFolder, true);
              if (partData) {
                authorStories.push({
                  id: story.id,
                  title: partData.title,
                  excerpt: partData.excerpt,
                  publishDate: partDate,
                  category: story.category,
                  tags: await this.tagDAO.getTagNames(story.tags || []),
                  readTime: partData.readTime,
                  coverImage: partData.coverImage,
                  featured: story.featured,
                  type: 'series',
                  series: {
                    name: seriesData.name,
                    partNumber: part.partNumber,
                    totalParts: seriesData.totalParts
                  },
                  slug: this.blogDAO.findRoutingKey(blogDb.routing, story.id, part.partFolder)
                });
              }
            }
          }
        }
      }

      // Sort stories
      authorStories.sort((a, b) => {
        let comparison = 0;
        
        switch (sortBy) {
          case 'date':
            comparison = new Date(a.publishDate) - new Date(b.publishDate);
            break;
          case 'title':
            comparison = a.title.localeCompare(b.title);
            break;
          case 'category':
            comparison = a.category.localeCompare(b.category);
            break;
          case 'readTime':
            comparison = (a.readTime || 0) - (b.readTime || 0);
            break;
          default:
            comparison = new Date(a.publishDate) - new Date(b.publishDate);
        }
        
        return sortOrder === 'desc' ? -comparison : comparison;
      });

      // Apply pagination
      const paginatedStories = limit || offset 
        ? authorStories.slice(offset, limit ? offset + limit : undefined)
        : authorStories;

      return paginatedStories;
    } catch (error) {
      console.error(`Failed to get author stories: ${authorSlug}`, error);
      return [];
    }
  }

  // Calculate comprehensive author statistics
  async calculateAuthorStats(authorSlug, stories = null) {
    try {
      if (!stories) {
        stories = await this.getAuthorStories(authorSlug, { includeUnpublished: false });
      }

      const stats = {
        totalStories: stories.length,
        totalReadTime: stories.reduce((sum, story) => sum + (story.readTime || 0), 0),
        featuredStories: stories.filter(story => story.featured).length,
        categoriesWritten: [...new Set(stories.map(story => story.category))],
        averageReadTime: 0,
        mostUsedTags: [],
        publicationFrequency: this.calculatePublicationFrequency(stories),
        firstPublished: null,
        lastPublished: null,
        seriesCount: 0,
        standaloneCount: 0
      };

      // Calculate average read time
      if (stories.length > 0) {
        stats.averageReadTime = Math.round(stats.totalReadTime / stories.length);
      }

      // Find publication dates
      const publishDates = stories
        .map(story => new Date(story.publishDate))
        .filter(date => !isNaN(date.getTime()))
        .sort((a, b) => a - b);

      if (publishDates.length > 0) {
        stats.firstPublished = publishDates[0].toISOString().split('T')[0];
        stats.lastPublished = publishDates[publishDates.length - 1].toISOString().split('T')[0];
      }

      // Count story types
      stats.seriesCount = [...new Set(stories.filter(s => s.type === 'series').map(s => s.id))].length;
      stats.standaloneCount = stories.filter(s => s.type === 'standalone').length;

      // Calculate most used tags
      const tagCount = {};
      stories.forEach(story => {
        (story.tags || []).forEach(tag => {
          tagCount[tag] = (tagCount[tag] || 0) + 1;
        });
      });

      stats.mostUsedTags = Object.entries(tagCount)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([tag, count]) => ({ tag, count }));

      return stats;
    } catch (error) {
      console.error(`Failed to calculate author stats: ${authorSlug}`, error);
      return {
        totalStories: 0,
        totalReadTime: 0,
        featuredStories: 0,
        categoriesWritten: [],
        averageReadTime: 0,
        mostUsedTags: [],
        publicationFrequency: 'unknown',
        firstPublished: null,
        lastPublished: null,
        seriesCount: 0,
        standaloneCount: 0
      };
    }
  }

  // Calculate publication frequency
  calculatePublicationFrequency(stories) {
    if (stories.length < 2) return 'insufficient-data';

    const publishDates = stories
      .map(story => new Date(story.publishDate))
      .filter(date => !isNaN(date.getTime()))
      .sort((a, b) => a - b);

    if (publishDates.length < 2) return 'insufficient-data';

    const totalDays = (publishDates[publishDates.length - 1] - publishDates[0]) / (1000 * 60 * 60 * 24);
    const averageDaysBetween = totalDays / (publishDates.length - 1);

    if (averageDaysBetween <= 7) return 'weekly';
    if (averageDaysBetween <= 14) return 'bi-weekly';
    if (averageDaysBetween <= 30) return 'monthly';
    if (averageDaysBetween <= 90) return 'quarterly';
    return 'occasional';
  }

  // Get author's areas of expertise based on their content
  async getAuthorExpertise(authorSlug, stories = null) {
    try {
      if (!stories) {
        stories = await this.getAuthorStories(authorSlug);
      }

      // Analyze categories
      const categories = {};
      stories.forEach(story => {
        categories[story.category] = (categories[story.category] || 0) + 1;
      });

      // Analyze tags for expertise
      const tags = {};
      stories.forEach(story => {
        (story.tags || []).forEach(tag => {
          tags[tag] = (tags[tag] || 0) + 1;
        });
      });

      // Get top categories and tags
      const topCategories = Object.entries(categories)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 5)
        .map(([category, count]) => ({ 
          area: category, 
          stories: count,
          percentage: Math.round((count / stories.length) * 100)
        }));

      const topTags = Object.entries(tags)
        .sort(([,a], [,b]) => b - a)
        .slice(0, 10)
        .map(([tag, count]) => ({ 
          skill: tag, 
          stories: count,
          level: this.getExpertiseLevel(count, stories.length)
        }));

      return {
        primaryCategories: topCategories,
        skills: topTags,
        writingStyle: this.analyzeWritingStyle(stories),
        contentTypes: this.analyzeContentTypes(stories)
      };
    } catch (error) {
      console.error(`Failed to get author expertise: ${authorSlug}`, error);
      return {
        primaryCategories: [],
        skills: [],
        writingStyle: 'unknown',
        contentTypes: []
      };
    }
  }

  // Determine expertise level based on story count
  getExpertiseLevel(storyCount, totalStories) {
    const percentage = (storyCount / totalStories) * 100;
    
    if (percentage >= 50) return 'expert';
    if (percentage >= 30) return 'experienced';
    if (percentage >= 15) return 'knowledgeable';
    return 'familiar';
  }

  // Analyze writing style based on content patterns
  analyzeWritingStyle(stories) {
    if (stories.length === 0) return 'unknown';

    const avgReadTime = stories.reduce((sum, story) => sum + (story.readTime || 0), 0) / stories.length;
    const hasLongForm = stories.some(story => (story.readTime || 0) > 15);
    const hasShortForm = stories.some(story => (story.readTime || 0) < 5);
    const hasSeries = stories.some(story => story.type === 'series');

    if (hasSeries && avgReadTime > 10) return 'comprehensive';
    if (hasLongForm && !hasShortForm) return 'detailed';
    if (!hasLongForm && hasShortForm) return 'concise';
    if (hasLongForm && hasShortForm) return 'versatile';
    if (avgReadTime > 8) return 'thorough';
    
    return 'balanced';
  }

  // Analyze content types the author creates
  analyzeContentTypes(stories) {
    const types = [];
    
    if (stories.some(s => s.type === 'series')) {
      types.push('Series');
    }
    
    if (stories.some(s => s.type === 'standalone')) {
      types.push('Articles');
    }
    
    const avgReadTime = stories.reduce((sum, s) => sum + (s.readTime || 0), 0) / stories.length;
    
    if (avgReadTime > 15) {
      types.push('Long-form');
    } else if (avgReadTime < 5) {
      types.push('Quick reads');
    }
    
    if (stories.some(s => s.tags && s.tags.includes('Tutorial'))) {
      types.push('Tutorials');
    }
    
    if (stories.some(s => s.tags && s.tags.includes('Guide'))) {
      types.push('Guides');
    }
    
    if (stories.some(s => s.tags && s.tags.includes('Analysis'))) {
      types.push('Analysis');
    }

    return types.length > 0 ? types : ['General'];
  }

  // AUTHOR DISCOVERY AND SEARCH

  // Get all authors with optional filtering
  async getAllAuthors(options = {}) {
    const { 
      sortBy = 'name', 
      sortOrder = 'asc',
      limit = null,
      minStories = 0
    } = options;

    try {
      const allAuthors = await this.authorDAO.getAllAuthors();
      
      // Filter by minimum stories
      const filteredAuthors = minStories > 0 
        ? allAuthors.filter(author => (author.storyCount || 0) >= minStories)
        : allAuthors;

      // Sort authors
      filteredAuthors.sort((a, b) => {
        let comparison = 0;
        
        switch (sortBy) {
          case 'name':
            comparison = (a.name || '').localeCompare(b.name || '');
            break;
          case 'storyCount':
            comparison = (a.storyCount || 0) - (b.storyCount || 0);
            break;
          case 'joinDate':
            comparison = new Date(a.joinDate || '1970-01-01') - new Date(b.joinDate || '1970-01-01');
            break;
          default:
            comparison = (a.name || '').localeCompare(b.name || '');
        }
        
        return sortOrder === 'desc' ? -comparison : comparison;
      });

      // Apply limit
      return limit ? filteredAuthors.slice(0, limit) : filteredAuthors;
    } catch (error) {
      console.error('Failed to get all authors:', error);
      return [];
    }
  }

  // Search authors by various criteria
  async searchAuthors(query, options = {}) {
    const { limit = 10, includeStats = false } = options;
    
    if (!query || query.trim().length < 2) {
      return [];
    }

    try {
      const searchResults = await this.authorDAO.searchAuthors(query);
      
      if (includeStats) {
        // Add statistics to search results
        const authorsWithStats = await Promise.all(
          searchResults.slice(0, limit).map(async (author) => {
            const stats = await this.calculateAuthorStats(author.slug);
            return { ...author, stats };
          })
        );
        return authorsWithStats;
      }

      return searchResults.slice(0, limit);
    } catch (error) {
      console.error(`Failed to search authors for "${query}":`, error);
      return [];
    }
  }

  // Get top authors by story count
  async getTopAuthors(limit = 10) {
    try {
      return await this.authorDAO.getTopAuthors(limit);
    } catch (error) {
      console.error('Failed to get top authors:', error);
      return [];
    }
  }

  // Get authors by expertise area
  async getAuthorsByExpertise(expertise, limit = 10) {
    try {
      return await this.authorDAO.getAuthorsByExpertise(expertise);
    } catch (error) {
      console.error(`Failed to get authors by expertise "${expertise}":`, error);
      return [];
    }
  }

  // UTILITY METHODS

  // Get default profile image path
  getDefaultProfileImage(authorSlug) {
    return `/blog/authors/${authorSlug}/profile.webp`;
  }

  // Validate author exists
  async authorExists(authorSlug) {
    return await this.authorDAO.authorExists(authorSlug);
  }

  // Get author's recent activity
  async getAuthorActivity(authorSlug, limit = 5) {
    try {
      const recentStories = await this.getAuthorStories(authorSlug, { 
        limit, 
        sortBy: 'date', 
        sortOrder: 'desc' 
      });

      return recentStories.map(story => ({
        type: 'published',
        title: story.title,
        date: story.publishDate,
        category: story.category,
        slug: story.slug
      }));
    } catch (error) {
      console.error(`Failed to get author activity: ${authorSlug}`, error);
      return [];
    }
  }

  // Get similar authors (based on content overlap)
  async getSimilarAuthors(authorSlug, limit = 5) {
    try {
      const targetAuthor = await this.getAuthorProfile(authorSlug);
      if (!targetAuthor) return [];

      const allAuthors = await this.getAllAuthors();
      const targetCategories = new Set(targetAuthor.stats?.categoriesWritten || []);
      const targetTags = new Set(targetAuthor.stats?.mostUsedTags?.map(t => t.tag) || []);

      const similarAuthors = await Promise.all(
        allAuthors
          .filter(author => author.slug !== authorSlug)
          .map(async (author) => {
            const profile = await this.getAuthorProfile(author.slug);
            if (!profile) return null;

            let similarity = 0;
            
            // Category overlap
            const authorCategories = new Set(profile.stats?.categoriesWritten || []);
            const categoryOverlap = [...targetCategories].filter(cat => authorCategories.has(cat)).length;
            similarity += categoryOverlap * 2;

            // Tag overlap
            const authorTags = new Set(profile.stats?.mostUsedTags?.map(t => t.tag) || []);
            const tagOverlap = [...targetTags].filter(tag => authorTags.has(tag)).length;
            similarity += tagOverlap;

            return similarity > 0 ? { ...author, similarity } : null;
          })
      );

      return similarAuthors
        .filter(Boolean)
        .sort((a, b) => b.similarity - a.similarity)
        .slice(0, limit);
    } catch (error) {
      console.error(`Failed to get similar authors for ${authorSlug}:`, error);
      return [];
    }
  }
}